import { ChangeEvent, Ref } from 'react';

import { InputBaseComponentProps } from '@mui/material';

const CNPJ_FORMAT = '##.###.###/####-##';

/**
 * Limpa o CNPJ formatado, removendo caracteres não alfanuméricos.
 * Os 12 primeiros caracteres são convertidos para maiúsculo e os 2 últimos caracteres são convertidos para número.
 */
const cleanCnpj = (value: string): string => {
    const alphanumeric = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const base = alphanumeric.slice(0, 12);
    const verifier = alphanumeric
        .slice(12)
        .replace(/[^0-9]/g, '')
        .slice(0, 2);

    return base + verifier;
};

/**
 * Limpa o CNPJ formatado e constrói a string com os caracteres da máscara.
 */
const maskCnpj = (value: string): string => {
    const clean = cleanCnpj(value);

    if (!clean) return '';

    let masked = '';
    let charIndex = 0;

    for (const maskChar of CNPJ_FORMAT) {
        if (maskChar === '#') {
            masked += charIndex < clean.length ? clean[charIndex++] : '_';
        } else {
            masked += maskChar;
        }
    }

    return masked;
};

/**
 * Retorna a posição do cursor na string mascarada após "cleanIndex" caracteres digitados, pulando os separadores
 */
const getCnpjCaretPosition = (cleanIndex: number): number => {
    let charCount = 0;

    for (let position = 0; position < CNPJ_FORMAT.length; position++) {
        if (CNPJ_FORMAT[position] === '#') {
            if (charCount === cleanIndex) return position;

            charCount++;
        }
    }

    return CNPJ_FORMAT.length;
};

type CnpjFormatAdapter = InputBaseComponentProps & {
    ref?: Ref<HTMLInputElement>;
};

export function CnpjFormatAdapter({ onChange, ref, value, ...other }: CnpjFormatAdapter) {
    const previousClean = cleanCnpj(String(value ?? ''));

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.target;
        const caret = input.selectionStart ?? input.value.length;
        const clean = cleanCnpj(input.value);
        const alphanumericBeforeCaret = (input.value.slice(0, caret).match(/[A-Za-z0-9]/g) ?? []).length;
        const cleanIndex = Math.min(alphanumericBeforeCaret, clean.length);
        const isDeletion = (event.nativeEvent as InputEvent).inputType?.startsWith('delete');

        // se a exclusão removeu apenas um separador, mantém o cursor onde o navegador deixou para não travar sobre ele
        const nextCaret = isDeletion && clean === previousClean ? caret : getCnpjCaretPosition(cleanIndex);

        input.value = maskCnpj(clean);
        input.setSelectionRange(nextCaret, nextCaret);

        if (onChange) {
            onChange({
                target: {
                    value: clean,
                },
            } as unknown as ChangeEvent<HTMLInputElement>);
        }
    };

    return <input {...other} ref={ref} value={maskCnpj(String(value ?? ''))} onChange={handleChange} />;
}
